import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiBarChart2, FiGrid, FiSettings, FiChevronRight } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import { IdentificationBadge, UsersThree, ClockCounterClockwise, Gear } from '@phosphor-icons/react';
// Menu chrome (top bar, item rows, accents) is shared with the Manage screen.
import './Setup.css';
import BottomNav from './BottomNav';
import PageHeader from './components/PageHeader';
import SocietyManagerModal from './components/SocietyManagerModal';
import ShutDownSocietyModal from './components/ShutDownSocietyModal';
import { useAuth } from './AuthContext';
import { useApi } from './useApi';
// Shared with the Manage screen rather than copied — this page and that one
// must agree on what you are in the society you are looking at.
import { getUserCapabilities, getEffectiveRole } from './Setup';

const bottomNavItems = [
  { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} />, needsSociety: true },
  { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} />, needsSociety: true },
  { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} />, needsSociety: true },
  { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
];

/**
 * The society itself and the people in it: which society you're in, who has
 * joined, their player profiles, and the record of what changed. Reached from
 * the Manage screen's "Manage the Society" button; every option states what it
 * does rather than relying on the user to tap and find out.
 */
const SocietyHub = () => {
  const navigate = useNavigate();
  const { currentSociety, userProfile, currentUser, setCurrentSociety, fetchUserProfile } = useAuth();
  const { authenticatedFetch } = useApi();

  const [isSocietyModalOpen, setIsSocietyModalOpen] = useState(false);
  const [isShutdownModalOpen, setIsShutdownModalOpen] = useState(false);
  const [isShuttingDown, setIsShuttingDown] = useState(false);
  const [shutdownError, setShutdownError] = useState('');
  const [memberCount, setMemberCount] = useState(null);

  const role = useMemo(
    () => getEffectiveRole(userProfile, currentSociety),
    [userProfile, currentSociety]
  );
  const caps = useMemo(() => getUserCapabilities(role), [role]);

  const openSocietyModal = useCallback(() => setIsSocietyModalOpen(true), []);

  const openShutdownModal = async () => {
    setShutdownError('');
    if (currentSociety) {
      try {
        const res = await authenticatedFetch(`/societies/${currentSociety.id}/members`);
        if (res.ok) setMemberCount((await res.json()).length);
      } catch {}
    }
    setIsShutdownModalOpen(true);
  };

  const handleShutdown = async () => {
    if (!currentSociety) return;
    setIsShuttingDown(true);
    setShutdownError('');
    try {
      const res = await authenticatedFetch(`/societies/${currentSociety.id}/archive`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setIsShutdownModalOpen(false);
        setCurrentSociety(null);
        if (currentUser) await fetchUserProfile(currentUser);
        navigate('/home');
      } else {
        setShutdownError(data.error || 'Failed to shut down society. Please try again.');
      }
    } catch {
      setShutdownError('An unexpected error occurred. Please try again.');
    }
    setIsShuttingDown(false);
  };

  const items = useMemo(() => {
    const societyId = currentSociety?.id;
    const result = [{
      id: 'profile',
      label: 'Society Profile',
      desc: caps.canManageSocieties
        ? 'Switch society, rename it, or share the invite code'
        : 'Switch society, or join and create one',
      icon: <Gear size={24} weight="duotone" />,
      onClick: openSocietyModal,
      accent: 'mi-society',
    }];

    if (caps.canViewMembers) {
      result.push({
        id: 'members',
        label: 'Members',
        desc: caps.canManageSocieties
          ? 'Approve requests, and promote members to admin'
          : 'Everyone who has joined this society',
        icon: <IdentificationBadge size={24} weight="duotone" />,
        route: societyId ? `/societies/${societyId}/members` : '#',
        accent: 'mi-society',
        needsSociety: true,
      });
    }

    if (caps.canViewPlayers) {
      result.push({
        id: 'players',
        label: 'Players',
        desc: caps.canManagePlayers
          ? 'Add players, set handicaps and view history'
          : 'Player profiles and handicaps',
        icon: <UsersThree size={24} weight="duotone" />,
        route: '/players',
        accent: 'mi-handicap',
        needsSociety: true,
      });
    }

    if (caps.canManageSocieties) {
      result.push({
        id: 'activity',
        label: 'Activity Log',
        desc: 'An audit trail of who changed what, and when',
        icon: <ClockCounterClockwise size={24} weight="duotone" />,
        route: societyId ? `/societies/${societyId}/activity` : '#',
        accent: 'mi-comps',
        needsSociety: true,
      });
    }

    return result;
  }, [caps, currentSociety, openSocietyModal]);

  const renderItem = (item) => {
    const disabled = item.needsSociety && !currentSociety;
    const className = `cc-item ${item.accent}${disabled ? ' disabled' : ''}`;
    const inner = (
      <>
        <span className="cc-item-icon">{item.icon}</span>
        <span className="cc-item-text">
          <span className="cc-item-label">{item.label}</span>
          <span className="cc-item-desc">{item.desc}</span>
        </span>
        <FiChevronRight className="cc-item-chevron" size={20} aria-hidden="true" />
      </>
    );

    if (item.onClick) {
      return (
        <button key={item.id} type="button" className={className} onClick={item.onClick}>
          {inner}
        </button>
      );
    }
    return (
      <Link
        key={item.id}
        to={disabled ? '#' : item.route}
        className={className}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        {inner}
      </Link>
    );
  };

  return (
    <div className="setup-container">
      <SocietyManagerModal
        isOpen={isSocietyModalOpen}
        onClose={() => setIsSocietyModalOpen(false)}
        onShutdownRequest={openShutdownModal}
      />
      {isShutdownModalOpen && (
        <ShutDownSocietyModal
          society={currentSociety}
          memberCount={memberCount}
          onClose={() => !isShuttingDown && setIsShutdownModalOpen(false)}
          onConfirm={handleShutdown}
          isSubmitting={isShuttingDown}
          errorMessage={shutdownError}
        />
      )}

      <PageHeader
        title="The Society"
        subtitle={currentSociety?.name || 'No society selected'}
        backTo="/setup"
        backLabel="Back to Manage"
      />

      <div className="cc-scroll-area">
        {caps.canManagePlayers && (
          <p className="info-note">
            <UsersThree size={15} weight="duotone" />
            <span>
              <strong>Players</strong> and <strong>members</strong> are separate. Add anyone as a
              player — no account needed — and enter them in competitions. If they sign up
              later, they're linked to that same profile.
            </span>
          </p>
        )}
        <div className="cc-menu">{items.map(renderItem)}</div>
      </div>

      <BottomNav menuItems={bottomNavItems} />
    </div>
  );
};

export default SocietyHub;
